import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import type { Coach as CoachModel } from '../model/Coach';
import type { Division, PlayoffStructure, PostseasonSeedSnapshotRow, Week } from '../model/Season';
import { buildPostseasonLock, canFinalizeRegularSeasonForPlayoffs } from '../utils/postseasonFinalize';

const Panel = styled.div`
    margin-top: 0.75em;
    padding: 1em;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    background: #f8fafc;
    font-size: 0.9rem;
    color: #334155;
`;

const Title = styled.h4`
    margin: 0 0 0.5em 0;
    font-size: 1rem;
    color: #0f172a;
`;

const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5em;
    margin-top: 0.75em;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
    border: none;
    border-radius: 6px;
    padding: 0.45em 0.9em;
    font-size: 0.88rem;
    cursor: pointer;
    ${p => {
        if (p.$variant === 'danger') {
            return `
                background: #fef2f2;
                color: #b91c1c;
                border: 1px solid #fecaca;
                &:hover { background: #fee2e2; }
            `;
        }
        if (p.$variant === 'secondary') {
            return `
                background: #fff;
                color: #334155;
                border: 1px solid #cbd5e1;
                &:hover { background: #f1f5f9; }
            `;
        }
        return `
            background: #0f766e;
            color: #fff;
            &:hover { background: #115e59; }
        `;
    }}
    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }
`;

const Meta = styled.p`
    margin: 0.35em 0 0;
    font-size: 0.82rem;
    color: #64748b;
`;

const SeedPreview = styled.ol`
    margin: 0.5em 0 0;
    padding-left: 1.25em;
    max-height: 10em;
    overflow-y: auto;
`;

export interface PostseasonFinalizePanelProps {
    seasonData: {
        divisions: Division[];
        weeks: Week[];
        playoffStructure?: PlayoffStructure;
        postseasonSeedsSnapshot?: PostseasonSeedSnapshotRow[];
        postseasonSeededAt?: string;
        postseasonLastRegularWeekIndex?: number;
    };
    leagueCoaches: CoachModel[];
    /** When false, panel is not shown (e.g. bracket section not active). */
    visible: boolean;
    onPersist: (next: {
        postseasonSeedsSnapshot: PostseasonSeedSnapshotRow[];
        postseasonSeededAt: string;
        postseasonLastRegularWeekIndex: number;
    }) => Promise<void>;
    onClearLock: () => Promise<void>;
}

export const PostseasonFinalizePanel: React.FC<PostseasonFinalizePanelProps> = ({
    seasonData,
    leagueCoaches,
    visible,
    onPersist,
    onClearLock
}) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const canFinalize = useMemo(() => canFinalizeRegularSeasonForPlayoffs(seasonData), [seasonData]);

    const previewRows = useMemo(() => {
        if (!canFinalize) return [];
        try {
            return buildPostseasonLock(seasonData, leagueCoaches).postseasonSeedsSnapshot;
        } catch {
            return [];
        }
    }, [seasonData, leagueCoaches, canFinalize]);

    if (!visible) return null;

    const locked = Boolean(
        seasonData.postseasonSeedsSnapshot &&
            seasonData.postseasonSeedsSnapshot.length > 0 &&
            seasonData.postseasonSeededAt
    );

    const handleFinalize = async () => {
        setError(null);
        setBusy(true);
        try {
            const lock = buildPostseasonLock(seasonData, leagueCoaches);
            await onPersist(lock);
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    };

    const handleClear = async () => {
        if (!window.confirm('Remove the playoff seed lock? The bracket will use live standings again until you finalize.')) {
            return;
        }
        setError(null);
        setBusy(true);
        try {
            await onClearLock();
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Panel data-testid="postseason-finalize-panel">
            <Title>Playoff seed lock</Title>
            <p style={{ margin: 0 }}>
                Lock the playoff field from <strong>current regular-season standings</strong> so the bracket does not change
                if older results are edited later. You can re-finalize after standings fixes, or clear the lock entirely.
            </p>
            {locked && (
                <Meta>
                    Locked{seasonData.postseasonSeededAt ? ` (${new Date(seasonData.postseasonSeededAt).toLocaleString()})` : ''}.
                    {typeof seasonData.postseasonLastRegularWeekIndex === 'number'
                        ? ` Last regular week index: ${seasonData.postseasonLastRegularWeekIndex}.`
                        : ''}
                </Meta>
            )}
            {canFinalize && previewRows.length > 0 && (
                <SeedPreview aria-label="Seeds from current standings">
                    {previewRows.map(r => (
                        <li key={r.teamId}>
                            Seed {r.seed}: {r.teamName} ({r.coachName})
                        </li>
                    ))}
                </SeedPreview>
            )}
            {error && (
                <p style={{ color: '#b91c1c', margin: '0.5em 0 0', fontSize: '0.85rem' }} role="alert">
                    {error}
                </p>
            )}
            <Actions>
                <Button type="button" disabled={!canFinalize || busy} onClick={handleFinalize}>
                    {locked ? 'Re-finalize from current standings' : 'Finalize regular season for playoffs'}
                </Button>
                <Button type="button" $variant="danger" disabled={!locked || busy} onClick={handleClear}>
                    Clear seed lock
                </Button>
            </Actions>
        </Panel>
    );
};
